import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { useAuth } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";
import {
  type AgentFeedbackRecord,
  type AgentThreadRecord,
  type AgentThreadService,
} from "@/shared/domain/agentThread";
import { downloadBlob } from "@/shared/domain/files/filesystem";
import { Env } from "@/shared/environment/Env";
import type { PaginatedResponse } from "@/shared/http";

import {
  buildFeedbackExportMarkdown,
  type FeedbackExportItem,
  feedbackExportFilename,
} from "./feedbackExport";
import { settleWithConcurrency } from "./settleWithConcurrency";
import { buildAdminFeedbackLink } from "./shareLinks";

/** Threads fetched at once, so a large selection doesn't flood the API. */
const THREAD_FETCH_CONCURRENCY = 4;

/** Where the finished bundle goes. */
export type FeedbackExportTarget = "download" | "clipboard";

/**
 * Turns selected feedback rows into the Markdown bundle built by
 * {@link buildFeedbackExportMarkdown}, then downloads it as a `.md` file or
 * copies it to the clipboard.
 *
 * Each row is exported as the newest copy in the admin-feedback query cache,
 * so a label, note or resolved state saved after the row was ticked is
 * included. Each distinct thread is fetched once, a few at a time, under the query key
 * the admin thread viewer uses, so a thread the viewer loaded within the
 * app-wide five-minute `staleTime` comes from cache. A thread that fails to
 * load doesn't fail the export: its entry says why. The returned function
 * rejects only when the clipboard write is refused.
 *
 * Call it straight from the click handler: Safari allows a clipboard write
 * only if it starts within the user gesture, so the write begins before the
 * threads load, with the bundle as a pending {@link ClipboardItem}.
 */
export function useFeedbackExport() {
  const queryClient = useQueryClient();
  const { agentThreadService } = useDomainServices();
  const { currentUser } = useAuth();

  return React.useCallback(
    (
      selected: AgentFeedbackRecord[],
      target: FeedbackExportTarget,
    ): Promise<void> => {
      const exportedAt = new Date();
      const markdown = buildMarkdown(
        { queryClient, agentThreadService, exportedBy: currentUser?.user_id },
        latestFeedbackRecords(queryClient, selected),
        exportedAt,
      );
      if (target === "clipboard") {
        return copyText(markdown);
      }
      return markdown.then((text) => {
        downloadBlob(
          feedbackExportFilename(exportedAt),
          new Blob([text], { type: "text/markdown" }),
        );
      });
    },
    [queryClient, agentThreadService, currentUser],
  );
}

/**
 * Replaces each record with its copy from the most recently fetched
 * admin-feedback page that holds it. The table's selection keeps the object
 * from when the row was ticked, and a triage save refetches the pages rather
 * than updating that object.
 */
function latestFeedbackRecords(
  queryClient: QueryClient,
  selected: AgentFeedbackRecord[],
): AgentFeedbackRecord[] {
  const pages = queryClient
    .getQueryCache()
    .findAll({ queryKey: ["adminFeedback"] })
    .sort((a, b) => a.state.dataUpdatedAt - b.state.dataUpdatedAt);
  const latest = new Map<string, AgentFeedbackRecord>();
  for (const page of pages) {
    const data = page.state.data as
      PaginatedResponse<AgentFeedbackRecord> | undefined;
    for (const record of data?.items ?? []) {
      latest.set(record.feedback_id, record);
    }
  }
  return selected.map((f) => latest.get(f.feedback_id) ?? f);
}

/**
 * Copies text that is still being built. Where ``ClipboardItem`` exists the
 * write starts now, inside the user gesture, with the text as a pending blob;
 * otherwise it falls back to ``writeText`` once the text is ready.
 */
function copyText(text: Promise<string>): Promise<void> {
  if (typeof ClipboardItem !== "undefined") {
    return navigator.clipboard.write([
      new ClipboardItem({
        "text/plain": text.then(
          (value) => new Blob([value], { type: "text/plain" }),
        ),
      }),
    ]);
  }
  return text.then((value) => navigator.clipboard.writeText(value));
}

interface ExportDeps {
  queryClient: QueryClient;
  agentThreadService: AgentThreadService;
  exportedBy: string | undefined;
}

/** Loads the selected rows' threads and renders the bundle. */
async function buildMarkdown(
  { queryClient, agentThreadService, exportedBy }: ExportDeps,
  selected: AgentFeedbackRecord[],
  exportedAt: Date,
): Promise<string> {
  const threadIds = [...new Set(selected.map((f) => f.thread_id))];
  const settled = await settleWithConcurrency(
    threadIds,
    THREAD_FETCH_CONCURRENCY,
    (threadId) =>
      queryClient.fetchQuery({
        queryKey: ["adminAgentThread", threadId],
        queryFn: ({ signal }) =>
          agentThreadService.getAgentThreadById(threadId, true, {
            abortSignal: signal,
          }),
      }),
  );
  const threadResults = new Map<
    string,
    PromiseSettledResult<AgentThreadRecord>
  >(threadIds.map((id, i) => [id, settled[i]]));

  const items: FeedbackExportItem[] = selected.map((feedback) => {
    const result = threadResults.get(feedback.thread_id);
    const adminLink = buildAdminFeedbackLink({
      sessionId: feedback.thread_id,
      feedbackId: feedback.feedback_id,
    });
    if (result?.status === "fulfilled") {
      return { feedback, thread: result.value, adminLink };
    }
    const reason: unknown = result?.reason;
    return {
      feedback,
      thread: null,
      threadError: reason instanceof Error ? reason.message : undefined,
      adminLink,
    };
  });

  return buildFeedbackExportMarkdown({
    items,
    environment: {
      appEnv: Env.appEnv,
      webAppUrl: `${window.location.origin}${Env.appBasePath}`,
      apiBaseUrl: Env.robotoServiceBaseUrl,
      gitCommitId: Env.gitCommitId,
    },
    exportedAt,
    exportedBy: exportedBy ?? "unknown",
  });
}
